import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useState, useEffect, createContext, useContext, useCallback, } from 'react';
import { ThemeContainer } from '@sas/nova-commons/theme';
import { connect } from 'react-redux';
import { getReportThemeParameters, resolveReportThemeOverrides, isSystemReportTheme, } from '@sas/nova-commons/design-tokens';
import { fetchThemeParameters, fetchOfflineThemeParameters } from './internal/ThemeService';
import { TransportState } from '../../store/transport/transportState';
import { bindActionCreators } from 'redux';
import { useHasHighContrastReportTheme, useLoadingTheme } from '../../config';
import { useBusyStateAnimationTokens } from '../utils/busyStateIndicatorTokens';
import { isNullish } from '@sas/nova-core/typeguards';
import cx from 'classnames';
import cn from './ReportThemeContainer.css.json';
import './ReportThemeContainer.css';
const LoadingThemeContext = createContext([
    getReportThemeParameters('light'),
    undefined,
]);
export const useLoadingThemeContext = () => {
    return useContext(LoadingThemeContext);
};
export const useLoadingBusyTokens = () => {
    const [, style] = useLoadingThemeContext();
    const busyTokens = useCallback(() => {
        const overrides = {};
        if (style === null || style === void 0 ? void 0 : style.backgroundColor) {
            overrides.bg = style.backgroundColor;
        }
        if (style === null || style === void 0 ? void 0 : style.textColor) {
            overrides.fg = style.textColor;
        }
        return overrides;
    }, [style]);
    const animationTokenFix = useBusyStateAnimationTokens();
    const animationTokens = useMemo(() => {
        const bg = style === null || style === void 0 ? void 0 : style.primary;
        if (!bg) {
            return animationTokenFix;
        }
        return () => (Object.assign(Object.assign({}, animationTokenFix()), { bg, 'animation-start': bg }));
    }, [style, animationTokenFix]);
    return [animationTokens, busyTokens];
};
function getLoadingThemeName(theme) {
    switch (theme) {
        case 'high-contrast':
            // highcontrast doesn't have a 2025 version
            return 'highcontrast2020';
        case 'dark':
            return 'dark2025';
        case 'light':
        default:
            return 'light2025';
    }
}
const ReportThemeContainerBase = ({ reportTheme, children, url, packageUri, themeOverrides, authenticated, actions, className, style, }) => {
    const defaultThemeName = getLoadingThemeName(useHasHighContrastReportTheme() ? 'high-contrast' : 'light');
    const defaultTheme = useMemo(() => getReportThemeParameters(defaultThemeName), [
        defaultThemeName,
    ]);
    const initialTheme = useMemo(() => (isNullish(reportTheme) ? defaultTheme : getReportThemeParameters(reportTheme)), [reportTheme, defaultTheme]);
    const [serverTheme, setServerTheme] = useState(null);
    const overrideTheme = useMemo(() => {
        return themeOverrides ? resolveReportThemeOverrides(themeOverrides) : null;
    }, [themeOverrides]);
    const currentTheme = overrideTheme || serverTheme || initialTheme;
    const loadingTheme = useLoadingTheme();
    const loadingValue = useMemo(() => {
        if (!loadingTheme) {
            // Avoiding initialTheme and serverTheme to prevent a flash of report theme during load
            return [overrideTheme !== null && overrideTheme !== void 0 ? overrideTheme : defaultTheme, undefined];
        }
        if (typeof loadingTheme === 'string') {
            return [getReportThemeParameters(getLoadingThemeName(loadingTheme)), undefined];
        }
        let theme = getReportThemeParameters(getLoadingThemeName(loadingTheme.baseTheme || 'light'));
        if (loadingTheme.fontFamily) {
            theme = Object.assign(Object.assign({}, theme), { overrides: Object.assign(Object.assign({}, theme.overrides), { fontFamily: loadingTheme.fontFamily }) });
        }
        return [theme, loadingTheme];
    }, [loadingTheme, overrideTheme, defaultTheme]);
    useEffect(() => {
        if (serverTheme && !reportTheme) {
            setServerTheme(null);
        }
    }, [serverTheme, reportTheme]);
    useEffect(() => {
        if (!reportTheme) {
            return;
        }
        const isLocalTheme = !!overrideTheme || isSystemReportTheme(reportTheme);
        if (isLocalTheme) {
            actions.setThemeLoaded(true);
            setServerTheme(null);
            return;
        }
        if (!authenticated) {
            return;
        }
        let ignoreResult = false;
        if (packageUri) {
            fetchOfflineThemeParameters(packageUri, reportTheme)
                .then((theme) => {
                if (ignoreResult) {
                    return;
                }
                setServerTheme(theme);
                actions.setThemeLoaded(true);
            })
                .catch(() => {
                if (ignoreResult) {
                    return;
                }
                actions.setTransportErrorKey({ key: 'ReportContainer.themeLoadFailed.txt' });
            });
        }
        else if (url) {
            fetchThemeParameters(url, reportTheme)
                .then((theme) => {
                if (ignoreResult) {
                    return;
                }
                setServerTheme(theme);
                actions.setThemeLoaded(true);
            })
                .catch(() => {
                if (ignoreResult) {
                    return;
                }
                actions.setTransportErrorKey({ key: 'ReportContainer.themeLoadFailed.txt' });
            });
        }
        else {
            return;
        }
        return () => {
            ignoreResult = true;
        };
    }, [authenticated, url, packageUri, reportTheme, overrideTheme, actions]);
    return (_jsx(ThemeContainer, Object.assign({ className: cx(cn.themeContainer, className), style: style, currentTheme: currentTheme }, { children: _jsx(LoadingThemeContext.Provider, Object.assign({ value: loadingValue }, { children: children })) })));
};
export const ReportThemeContainer = connect((state, _props) => ({
    reportTheme: state.bird.reportTheme,
    authenticated: state.transport.reportLoaded === true,
}), (dispatch) => ({ actions: bindActionCreators(TransportState.actions, dispatch) }))(ReportThemeContainerBase);
